import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';

// Kiosk tablets (Frigg Kiosk Android app at schools): what each tablet is
// doing right now, and a remote restart. The tablets report every minute to
// the backend (POST /kiosk/heartbeat, controllers/kioskDeviceController.js),
// which is the only remote channel to them.
interface KioskTablet {
  deviceId: string;
  systemId: string;
  machineName: string | null;
  model: string | null;
  version: string | null;
  build: number | null;
  state: string;
  offline: boolean;
  payEnabled: boolean;
  lockMode: 'locked' | 'pinned' | 'none' | null;
  screenLock?: boolean;
  lastSeen: number | null;
  lastCommandDoneAt: number | null;
  command: { id: string, type: 'reload' | 'reboot', at: number | null } | null;
}

// A tablet reports every minute: after 3 minutes without a word it is shown
// as not responding.
const SILENT_AFTER_MS = 3 * 60_000;
const REFRESH_MS = 30_000;

const STATE_TEXT: { [state: string]: string } = {
  loading: 'Loading (spinner)',
  unavailable: 'Closed / offline screen',
  browsing: 'Ready for customers',
  paying: 'Paying',
  tapping: 'Waiting for card',
  approved: 'Payment approved',
  receipt: 'SMS receipt',
  failed: 'Payment failed',
};

const MODELS: { [model: string]: string } = {
  'SM-X306B': 'Galaxy Tab Active5 (8")',
  'SM-X356B': 'Galaxy Tab Active5 Pro (10")',
};

@Component({
  selector: 'app-kiosk-tablets',
  templateUrl: './kiosk-tablets.component.html',
  styleUrls: ['./kiosk-tablets.component.css']
})
export class KioskTabletsComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  message = '';
  tablets: KioskTablet[] = [];
  sending: { [deviceId: string]: boolean } = {};
  confirming: { [deviceId: string]: 'reload' | 'reboot' | null } = {};
  private timer: any;

  constructor(private http: HttpClient, private auth: AuthService) { }

  ngOnInit() {
    this.refresh();
    this.timer = setInterval(() => this.refresh(), REFRESH_MS);
  }

  ngOnDestroy() {
    clearInterval(this.timer);
  }

  async refresh() {
    try {
      const res: any = await firstValueFrom(this.http.get(`${environment.apiUrl}/widgets/kioskDevices`, { headers: await this.headers() }));
      this.tablets = (res && res.devices) || [];
      this.error = '';
    } catch (err: any) {
      this.error = err?.error?.message || 'Could not load the tablets.';
    }
    this.loading = false;
  }

  silent(t: KioskTablet) {
    return !t.lastSeen || Date.now() - t.lastSeen > SILENT_AFTER_MS;
  }

  stateText(t: KioskTablet) {
    if (this.silent(t)) return 'Not responding';
    if (t.state === 'unavailable' && t.offline) return 'Offline (no internet)';
    return STATE_TEXT[t.state] || t.state;
  }

  stateClass(t: KioskTablet) {
    if (this.silent(t) || t.state === 'loading' || t.offline) return 'bad';
    if (t.state === 'unavailable' || !t.payEnabled || t.lockMode !== 'locked' || t.screenLock) return 'warn';
    return 'ok';
  }

  modelText(t: KioskTablet) {
    return (t.model && MODELS[t.model]) || t.model || 'Tablet';
  }

  // Two taps, so a stray click never restarts a tablet in use.
  async send(t: KioskTablet, type: 'reload' | 'reboot') {
    if (this.confirming[t.deviceId] !== type) {
      this.confirming[t.deviceId] = type;
      setTimeout(() => { if (this.confirming[t.deviceId] === type) this.confirming[t.deviceId] = null; }, 5000);
      return;
    }
    this.confirming[t.deviceId] = null;
    this.sending[t.deviceId] = true;
    this.message = '';
    try {
      await firstValueFrom(this.http.post(`${environment.apiUrl}/widgets/kioskDevices/${encodeURIComponent(t.deviceId)}/command`,
        { type }, { headers: await this.headers() }));
      this.message = `${type === 'reboot' ? 'Restart tablet' : 'Restart app'} sent to ${t.machineName || t.deviceId}. ` +
        'It runs within a minute (after a payment in progress).';
      await this.refresh();
    } catch (err: any) {
      this.error = err?.error?.message || 'Could not send the command.';
    }
    this.sending[t.deviceId] = false;
  }

  private async headers() {
    return new HttpHeaders({ Authorization: 'Bearer ' + await this.auth.getToken() });
  }
}
